// client/src/components/pages/AmendmentsAllocateTransactions/InlineEditableCell.tsx
// (converted from .js, ST-00221). One cell of Table Edit Mode on the Amendments page.
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { isValidWeight, isWeightField, weightMessage } from './amendmentFields';

const EDITABLE_FIELDS: ReadonlySet<string> = new Set([
  'Delivery No',
  'Order No',
  'Revision No',
  'Ticket Type',
  'Dispatch Organisation',
  'Dispatch Location',
  'Receipt Organisation',
  'Receipt Location',
  'First Weight',
  'Second Weight',
  'Nett Weight',
  'Delivery Weight',
  'Variance Weight',
  'Haulier',
  'Reg No',
  'Driver',
  'Product',
  'Ref No',
  'Product Ref.',
  'Trailer Reg 1',
  'Trailer Reg 2',
  'Security Seal 1',
  'Security Seal 2',
  'First Weight Timestamp',
  'Second Weight Timestamp',
  'Tag1',
  'Tag2',
  'Tag3',
  'Tag4',
]);

// The editable weigh timestamps (Time Between Weights and ImportTimestamp are not editable here).
const TIMESTAMP_FIELDS: ReadonlySet<string> = new Set([
  'First Weight Timestamp',
  'Second Weight Timestamp',
]);

const isEditable = (accessor: string | undefined): boolean =>
  !!accessor && EDITABLE_FIELDS.has(accessor);

export type NavigateDirection = 'left' | 'right' | 'down';

/** A cell value as react-table hands it over. */
export type InlineCellValue = string | number | boolean | null | undefined;

export interface InlineEditableCellProps {
  value: InlineCellValue;
  /** The react-table row (only `original.TicketUID` is read). */
  row: { original?: { TicketUID?: string | null } | null };
  /** The react-table column (its id, or its string accessor). */
  column: { id?: string; accessor?: unknown };
  editMode: boolean;
  /** `${ticketUID}::${field}` → new value. */
  pendingEdits?: Map<string, string> | null;
  /** A new value, or null to drop the pending edit (back to the original). */
  onCellEdit?: (ticketUID: string, field: string, newValue: string | null) => void;
  onNavigate?: (ticketUID: string, field: string, direction: NavigateDirection) => void;
}

const InlineEditableCell = ({
  value,
  row,
  column,
  editMode,
  pendingEdits,
  onCellEdit,
  onNavigate,
}: InlineEditableCellProps): ReactElement | null => {
  const accessor =
    column.id || (typeof column.accessor === 'string' ? column.accessor : undefined) || '';
  const ticketUID = row.original?.TicketUID ?? '';
  const editKey = `${ticketUID}::${accessor}`;
  const pendingValue = pendingEdits?.get(editKey);
  const hasPendingEdit = pendingValue !== undefined;
  const displayValue = hasPendingEdit ? pendingValue : (value ?? '');

  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(String(displayValue));
  // Why the last entry was not taken (a blank or non-numeric weight, AMD-21)
  const [refusal, setRefusal] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(String(displayValue));
  }, [displayValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  /** Ends editing; false when the entry was refused (nothing queued). */
  const commitEdit = useCallback((): boolean => {
    setIsEditing(false);
    const trimmed = localValue.trim();
    const original = String(value ?? '');
    // A blank or unparsable weight was saved as NULL, so the data view showed the raw source
    // weight again (AMD-21). Refuse it here, like the Bulk Amend modal, and keep what was there.
    if (isWeightField(accessor) && trimmed !== original && !isValidWeight(trimmed)) {
      setRefusal(`${weightMessage(accessor)} Not changed.`);
      setLocalValue(String(displayValue));
      return false;
    }
    setRefusal(null);
    if (trimmed !== original) {
      onCellEdit?.(ticketUID, accessor, trimmed);
    } else if (hasPendingEdit) {
      onCellEdit?.(ticketUID, accessor, null);
    }
    return true;
  }, [localValue, value, ticketUID, accessor, onCellEdit, hasPendingEdit, displayValue]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const taken = commitEdit();
        if (taken && onNavigate) {
          onNavigate(
            ticketUID,
            accessor,
            e.key === 'Tab' && e.shiftKey ? 'left' : e.key === 'Tab' ? 'right' : 'down'
          );
        }
      } else if (e.key === 'Escape') {
        setLocalValue(String(value ?? ''));
        setIsEditing(false);
      }
    },
    [commitEdit, onNavigate, ticketUID, accessor, value]
  );

  if (!editMode || !isEditable(accessor)) {
    if (hasPendingEdit) {
      return (
        <span className="relative">
          <span className="text-primary-700 font-medium">{String(displayValue)}</span>
          <span
            className="absolute -top-0.5 -right-1.5 w-1.5 h-1.5 bg-amber-400 rounded-full"
            title={`Was: ${value ?? '(blank)'}`}
          />
        </span>
      );
    }
    return null;
  }

  if (isEditing) {
    const inputType = isWeightField(accessor)
      ? 'number'
      : TIMESTAMP_FIELDS.has(accessor)
        ? 'datetime-local'
        : 'text';
    return (
      <input
        ref={inputRef}
        type={inputType}
        step={inputType === 'number' ? 'any' : undefined}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        className={`w-full px-1.5 py-0.5 text-xs border rounded outline-none transition-colors ${
          hasPendingEdit
            ? 'border-amber-400 bg-amber-50 focus:ring-1 focus:ring-amber-400 focus:border-amber-400'
            : 'border-primary-300 bg-surface focus:ring-1 focus:ring-primary-400 focus:border-primary-400'
        }`}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        setRefusal(null);
        setIsEditing(true);
      }}
      className={`cursor-text px-1.5 py-0.5 -mx-1.5 -my-0.5 rounded min-h-[22px] transition-colors ${
        refusal
          ? 'ring-1 ring-inset ring-red-300'
          : hasPendingEdit
            ? 'bg-amber-50 ring-1 ring-inset ring-amber-300'
            : 'hover:bg-primary-50/50 hover:ring-1 hover:ring-inset hover:ring-primary-200'
      }`}
      title={hasPendingEdit ? `Was: ${value ?? '(blank)'}` : 'Click to edit'}
    >
      <span className={hasPendingEdit ? 'text-primary-700 font-medium' : ''}>
        {String(displayValue) ? (
          String(displayValue)
        ) : (
          <span className="text-slate-300 italic">empty</span>
        )}
      </span>
      {hasPendingEdit && (
        <span className="absolute -top-0.5 -right-1.5 w-1.5 h-1.5 bg-amber-400 rounded-full" />
      )}
      {refusal && (
        <span
          role="alert"
          className="block mt-0.5 max-w-[16rem] whitespace-normal text-[10px] leading-snug text-red-600"
        >
          {refusal}
        </span>
      )}
    </div>
  );
};

InlineEditableCell.isEditable = isEditable;
InlineEditableCell.EDITABLE_FIELDS = EDITABLE_FIELDS;

export default InlineEditableCell;
